import { useCallback, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { SocketContext } from "./SocketContext";
import { useAuth } from "../hooks/useAuth";
import { canRefreshDestinations, fetchDestinationMetadata } from "../helpers/destinationMetadata";
import { mergeDestinationZonesFromRequest } from "../actions/destinationsAction";
import { showBackendMessage } from "../helpers/messagesHelper";
import Messages from "../localization/Messages";

/** Single application listener; row timezone selectors remain read-only. */
export default function DestinationRefreshListener() {
    const socket = useContext(SocketContext);
    const { user } = useAuth();
    const dispatch = useDispatch();
    const intl = useIntl();
    const refresh = useCallback(
        (fresh = false) => {
            if (!user.token || !canRefreshDestinations(user)) return;
            fetchDestinationMetadata(user, fresh)
                .then(destinations => dispatch(mergeDestinationZonesFromRequest(destinations)))
                .catch(() =>
                    dispatch(showBackendMessage(intl, "error", "fetching", Messages.destinations)),
                );
        },
        [user, dispatch, intl],
    );

    useEffect(() => {
        refresh();
        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [refresh]);
    useEffect(() => {
        if (socket?.isOpen) refresh();
    }, [socket?.isOpen, refresh]);
    useEffect(() => {
        if (!socket?.lastMessage) return;
        try {
            if (JSON.parse(socket.lastMessage.data).entity === "Destination") refresh(true);
        } catch {
            /* Non-JSON socket control frames contain no destination invalidation. */
        }
    }, [socket?.lastMessage, refresh]);
    return null;
}
